import type { Editor } from "@tiptap/core";
import { cn } from "cn";
import { memo } from "react";
import {
  BwDeleteLine,
  BwPencilLine,
  BwWorldNetwork,
} from "@/components/ui/icons";
import { useEditorCustomState } from "../hooks/use-editor-custom-state";
import { BubbleMenuPortal } from "./bubble-menu-portal";
import { FormatToolBar } from "./format-tool-bar";

export const BubbleMenu = memo(
  ({ editor, openInlineMenu }: { editor: Editor; openInlineMenu: boolean }) => {
    const { existingLink, openLinkDialog } = useEditorCustomState(editor);

    const showModal = () => {
      if (!editor) return;
      editor.commands.extendMarkRange("link");
      editor.commands.toggleLinkDialog(true);
      editor.commands.toggleBubbleLink(false);
    };
    const setUnLink = () => {
      if (!editor) return;
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      editor.commands.toggleBubbleLink(false);
    };

    if (!editor) return null;

    return (
      <BubbleMenuPortal
        data-test="bubble-menu"
        data-inline-edit-keep-open=""
        className={cn(
          /* sorted */
          "hidden",
          "rounded-xl",
          "md:block",
          openInlineMenu && !editor.isActive("link") && "md:hidden",
        )}
        shouldShow={({ view }) => {
          const selection = view.state.selection;
          const isOpen = !selection.empty && !openLinkDialog;
          return isOpen;
        }}
        options={{
          placement: "top",
        }}
        editor={editor}
      >
        {!editor.isActive("link") ? (
          // // Bubble Text
          <FormatToolBar
            editor={editor}
            className="bg-bg-light border border-border-dark-low rounded-2xl"
          />
        ) : (
          // Bubble Link
          <div
            className={cn(
              /* sorted */
              "flex flex-row items-center justify-between",
              "w-83 h-8",
              "px-3",
              "bg-bg-light rounded-lg border border-border-dark-low",
              "pointer-events-auto",
            )}
          >
            <div className="flex flex-row items-center gap-2">
              <BwWorldNetwork size={16} className="shrink-0" />
              <span
                className="max-w-81 text-text-dark-max line-clamp-1 break-all"
                title={existingLink}
              >
                {existingLink}
              </span>
            </div>
            <div className="flex flex-row items-center gap-2">
              <div className="cursor-pointer" onClick={showModal}>
                <BwPencilLine size={16} />
              </div>
              <div className="cursor-pointer" onClick={setUnLink}>
                <BwDeleteLine size={16} className="text-text-negative" />
              </div>
            </div>
          </div>
        )}
      </BubbleMenuPortal>
    );
  },
);
